import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ObjectDocumentObjectType, UserType, WorkflowOrganisationAccessLevel } from "../../constants";
import { TaskConstants } from "../../constants/workflow/TaskConstants";
import { SelectData, TaskPanelData, UploadedDocument, WorkflowStepModel } from "../../models";
import { GetTaskBoardResponse, SearchWorkflowTasksResponse } from "../../service/query";
import { ServiceError } from "../../service/Shared";
import { useAuth } from "../../useAuth";
// Deep import, not the utils barrel: that pulls in service/identity (ESM-only `mem`) and breaks this hook's tests.
import { useDocumentUploadPermission } from "../../utils/hooks/useDocumentUploadPermission";
import { ObjectAttachment, useObjectAttachments } from "../../utils/hooks/useObjectAttachments";
import { getActivityDashboardTabRoute } from "../../utils/routes";
import { TaskTagsObjectType } from "../../widget";
import { resolveTaskPanelMode } from "./taskPanelMode";
import { resolveTaskParentKind, TASK_PARENT_POLICY, TaskParentKind } from "./taskParentPolicy";
import { useTaskAccess } from "./useTaskAccess";
import { useTaskActivityOptions } from "./useTaskActivityOptions";
import { useTaskStepOptions } from "./useTaskStepOptions";
import { useTaskSubmit } from "./useTaskSubmit";
import { TaskTag, useTaskTags } from "./useTaskTags";
import { useTaskVisibility } from "./useTaskVisibility";

// Parent object of a general task, used for objectType/objectUuid on create/update.
interface GeneralTaskParent {
  type: string | null;
  uuid: string | null;
}

// Note - We can't use context here as this component is used in multiple places
interface UseWorkflowTaskPanelProps {
  task?: SearchWorkflowTasksResponse["results"][0];
  currentStepUuid: string | undefined;
  workflow?: GetTaskBoardResponse["results"][number];
  workflowSteps?: WorkflowStepModel[];
  // Create-mode task type (REQ-010). Ignored on edit, where the type is derived from task.step.
  isGeneralTask?: boolean;
  /*
    Parent of a general task created without a workflow — a project with no workflow applied still has
    general tasks, and they hang off it through objectType/objectUuid. Ignored once a workflow supplies it.
  */
  parentObjectType?: string;
  parentObjectUuid?: string;

  show: boolean;
  refresh: boolean;
  handleRefresh: () => void;

  onClose: () => void;
}

export interface UseWorkflowTaskPanelReturnData {
  isCreateTask: boolean;
  // True when the parent has no project to pick an audience from, so visibility is the creating org alone.
  isVisibilityPinnedToCurrentOrg: boolean;
  // True when the parent fixes the owner organisation to the acting org, so the picker has nothing to offer.
  ownerOrganisationIsFixed: boolean;
  isGeneralTask: boolean;
  currentUserType: UserType;
  // Parent object type, used for the chip above the title. Undefined when unrecognised.
  parentObjectType: TaskTagsObjectType | undefined;

  workflowOrganisationAccessLevel: WorkflowOrganisationAccessLevel | undefined;
  hasFullEditAccess: boolean;

  stepSelectData: SelectData;
  // NOTE - hiding this until phase 3 KAN-7325 - https://kanaearth.atlassian.net/browse/KAN-7325
  // phaseKeyDatesSelectData: SelectData;
  organisationsSelectData: SelectData;
  visibilityOrganisationsSelectData: SelectData;
  activitySelectData: SelectData;

  errors: ServiceError[] | undefined;
  setErrors: Dispatch<SetStateAction<ServiceError[] | undefined>>;

  currentTask: TaskPanelData;
  setCurrentTask: Dispatch<SetStateAction<TaskPanelData>>;

  // Tags (read/link/create) for the open task.
  tags: TaskTag[];
  linkedTags: TaskTag[];
  availableTags: TaskTag[];
  setNewTagName: (value: string) => void;
  handleToggleTag: (uuid: string) => void;
  handleRemoveTag: (uuid: string) => void;
  handleAddNewTag: () => void;

  // Attachments (documents linked to the open task).
  attachments: ObjectAttachment[];
  isAttachmentsLoading: boolean;
  canAddAttachment: boolean;
  canRemoveAttachment: boolean;
  attachmentParentType: "project" | "deal" | "landholding" | undefined;
  attachmentParentUuid: string | null;
  /*
    An upload outlives the panel, so the session it was started in is taken up front by beginUpload and handed
    back with the documents — see useObjectAttachments.
  */
  beginUpload: () => number;
  handleDocumentsUploaded: (documents: UploadedDocument[], uploadToken: number) => Promise<void>;
  handleRemoveAttachment: (documentUuid: string) => Promise<void>;
  handleDownloadAttachment: (documentUuid: string) => Promise<void>;

  isTitleEditable: boolean;
  setIsTitleEditable: Dispatch<SetStateAction<boolean>>;

  showDeleteTaskModal: boolean;
  setShowDeleteTaskModal: Dispatch<SetStateAction<boolean>>;

  showCompletePhaseModal: boolean;
  setShowCompletePhaseModal: Dispatch<SetStateAction<boolean>>;

  updateStepsSelectDataByPhase: (phase: string) => void;
  handleLinkedActivityOnClick: () => void;
  handleCloseTaskPanel: () => void;
  handleSubmit: () => Promise<void>;
  isHandleSubmitLoading: boolean;
  defaultSelectedOrgTypeVisibility: string | undefined;
}

/*
  Builds the TaskPanelData for an existing task response (used for both the initial state and the
  show-effect refresh). Step is nullable for general tasks (KAN-9500).

  Visibility is not on the task response — useTaskVisibility resolves it from the task's own ACL — so a
  re-seed has to be handed the audience already resolved for this task. Defaulting it to empty would blank
  the field on every re-seed, and the ACL is only re-read when the task or the panel's open state changes.
*/
const mapTaskToPanelData = (
  task: SearchWorkflowTasksResponse["results"][0],
  resolvedVisibility: string[] | null = null
): TaskPanelData => ({
  deadlineDate: task.deadlineDate,
  isMandatory: task.isMandatory,
  isMilestone: task.isMilestone,
  keyDate: task.keyDate,
  status: task.status,
  repeat: task.repeat,
  description: task.description,
  name: task.name,
  visibility: resolvedVisibility,
  assignedToUserUuid: task.assignee?.uuid ?? null,
  assignedToUserName: task.assignee?.name ?? null,
  ownerOrganisationUuid: task.ownerOrganisation.uuid,
  relateToActivity: task.objectType === "Activity",
  activityUuid: task.objectType === "Activity" ? task.objectUuid : null,
  objectType: task.objectType,
  objectUuid: task.objectUuid,
  stepUuid: task.step?.uuid ?? null,
  phase: task.step?.phase ?? null,
  uuid: task.uuid,
  rowVersion: task.rowVersion,
  resolution: task.resolution,
  reminderDate: task.reminderDate,
  reminderType: task.reminderType,
  addReminder: task.reminderDate != null,
});

export const useWorkflowTaskPanel = ({
  task,
  currentStepUuid,
  workflow,
  isGeneralTask: isGeneralTaskProp,
  parentObjectType: parentObjectTypeProp,
  parentObjectUuid: parentObjectUuidProp,
  show,
  refresh,
  handleRefresh,

  onClose,
}: UseWorkflowTaskPanelProps): UseWorkflowTaskPanelReturnData => {
  const navigate = useNavigate();
  const { currentOrganisationUuid, currentUserType, user } = useAuth();
  const [errors, setErrors] = useState<ServiceError[]>();
  const [isTitleEditable, setIsTitleEditable] = useState(false);

  // NOTE - hiding this for now KAN-7325 - https://kanaearth.atlassian.net/browse/KAN-7325
  // const [phaseKeyDatesSelectData, setPhaseKeyDatesSelectData] = useState<SelectData>([]);

  // What the panel is doing (create vs edit) and which kind of task it is doing it to. Everything that
  // used to branch on `task === undefined` or on the isGeneralTask boolean narrows on this instead.
  const mode = useMemo(() => resolveTaskPanelMode(task, isGeneralTaskProp), [task, isGeneralTaskProp]);
  const isGeneralTask = mode.taskType === "general";

  const [currentTask, setCurrentTask] = useState<TaskPanelData>(
    mode.kind === "create" ? TaskConstants.DEFAULT_TASK : mapTaskToPanelData(mode.task)
  );

  const [showCompletePhaseModal, setShowCompletePhaseModal] = useState(false);
  const [showDeleteTaskModal, setShowDeleteTaskModal] = useState(false);

  const {
    tags,
    linkedTags,
    availableTags,
    setNewTagName,
    handleToggleTag,
    handleRemoveTag,
    handleAddNewTag,
    saveTags,
  } = useTaskTags(currentTask.uuid ?? undefined);

  // Parent (Project / Deal / Landholding) of a general task — objectType/objectUuid on write.
  const generalTaskParent = useMemo<GeneralTaskParent>(() => {
    // An existing general task already stores its parent on objectType/objectUuid, so read it
    // back from there — an update has to resend it or the link is cleared. The workflow object
    // is the fallback for a task whose own relation is empty.
    if (mode.kind === "edit") {
      const { task: editedTask } = mode;
      if (isGeneralTask && editedTask.objectUuid) return { type: editedTask.objectType, uuid: editedTask.objectUuid };
      return { type: editedTask.workflowObjectType, uuid: editedTask.workflowObjectUuid };
    }
    /*
      Creating a general task: the host's explicit parent wins. It names the object the panel was opened
      from, whereas the workflow in state can still be a previously selected one whose load has not been
      superseded yet — parenting the task to that would file it against the wrong project.
    */
    if (isGeneralTask && parentObjectTypeProp && parentObjectUuidProp) {
      return { type: parentObjectTypeProp, uuid: parentObjectUuidProp };
    }
    // Project before group: a project workflow can also carry the landholding group it belongs to, and a
    // task created from that board has to land on the project so it shows up in the view it was added from.
    if (workflow?.dealUuid) return { type: "Deal", uuid: workflow.dealUuid };
    if (workflow?.projectUuid) return { type: "Project", uuid: workflow.projectUuid };
    if (workflow?.groupUuid) return { type: "Landholding", uuid: workflow.groupUuid };
    // No workflow to derive the parent from, so fall back to the one the host supplied.
    if (parentObjectTypeProp && parentObjectUuidProp) {
      return { type: parentObjectTypeProp, uuid: parentObjectUuidProp };
    }
    return { type: null, uuid: null };
  }, [mode, workflow, isGeneralTask, parentObjectTypeProp, parentObjectUuidProp]);

  /*
    The one place the parent kind is decided; everything parent-driven reads its policy rather than
    re-deriving the kind. A deal workflow identifies a deal parent on its own: a general task on a deal has
    no workflow to read `dealUuid` off — the panel discards a workflow that isn't the task's own — so its
    Deal parent is what identifies it, and either route lands on the same kind.
  */
  const parentKind = useMemo<TaskParentKind | undefined>(
    () => (workflow?.dealUuid != null ? "deal" : resolveTaskParentKind(generalTaskParent.type)),
    [workflow?.dealUuid, generalTaskParent.type]
  );
  // Undefined for a parent the overlay cannot hang off (e.g. an activity relation): no parent-driven feature.
  const parentPolicy = parentKind ? TASK_PARENT_POLICY[parentKind] : undefined;

  /*
    The project whose organisations populate the Visibility picker, for the parents that take their audience
    from one. A workflow task reads it off the workflow; a general task has no workflow, so it falls back to
    its own Project parent.
  */
  const visibilityProjectUuid =
    parentPolicy?.visibility === "projectOrganisations"
      ? (workflow?.projectUuid ?? (parentKind === "project" ? generalTaskParent.uuid : null))
      : null;

  // The parent chip covers exactly the object types the overlay can hang off.
  const parentObjectType: TaskTagsObjectType | undefined = parentKind;

  // No project to choose an audience from ⇒ the creating org alone, and the field is hidden.
  const isVisibilityPinnedToCurrentOrg = parentPolicy?.visibility === "currentOrgOnly";

  // Only a deal fixes the owner organisation; the rest keep whatever the task carries.
  const ownerOrganisationIsFixed = parentPolicy?.ownerOrganisation === "currentOrg";

  // The parent a document is uploaded against. Undefined ⇒ this parent has no document store.
  const attachmentParentType = parentKind;

  const {
    attachments,
    isAttachmentsLoading,
    beginUpload,
    handleDocumentsUploaded,
    handleRemoveAttachment,
    handleDownloadAttachment,
    saveAttachments,
    resetAttachments,
  } = useObjectAttachments({
    objectUuid: currentTask.uuid ?? undefined,
    objectType: ObjectDocumentObjectType.WORKFLOW_TASK,
    documentSource: parentPolicy?.documents.source,
    documentScope: parentPolicy?.documents.scope,
    parentUuid: generalTaskParent.uuid,
    subjectLabel: "task",
  });

  const { stepSelectData, updateStepsSelectDataByPhase, resetStepOptions } = useTaskStepOptions(workflow);

  const activitySelectData = useTaskActivityOptions(workflow, refresh);

  const { visibilityOrganisationsSelectData, defaultSelectedOrgTypeVisibility, generalTaskCanEdit, resetVisibility } =
    useTaskVisibility({
      mode,
      visibilityProjectUuid,
      currentOrganisationUuid,
      show,
      refresh,
      setCurrentTask,
    });

  const { organisationsSelectData, hasFullEditAccess, effectiveAccessLevel, resetAccess } = useTaskAccess({
    mode,
    isGeneralTask,
    workflow,
    currentOrganisationUuid,
    generalTaskCanEdit,
  });

  const getTaskVisibility = (): string[] => {
    if (isVisibilityPinnedToCurrentOrg && currentOrganisationUuid) {
      return [currentOrganisationUuid];
    }

    return currentTask.visibility ?? [];
  };

  const resetTemplateTaskPanel = (): void => {
    setIsTitleEditable(false);
    setShowCompletePhaseModal(false);
    setShowDeleteTaskModal(false);
    setCurrentTask(TaskConstants.DEFAULT_TASK);
    setErrors([]);
    resetAccess();
    resetVisibility();
    resetStepOptions();
    resetAttachments();
  };

  const handleCloseTaskPanel = (): void => {
    resetTemplateTaskPanel();
    onClose();
  };

  const handleLinkedActivityOnClick = (): void => {
    if (currentTask.activityUuid)
      navigate(getActivityDashboardTabRoute(currentTask.activityUuid, "tasks", currentUserType));
  };

  const { handleSubmit, isHandleSubmitLoading } = useTaskSubmit({
    mode,
    isGeneralTask,
    currentTask,
    generalTaskParent,
    ownerOrganisationIsFixed,
    currentOrganisationUuid,
    getTaskVisibility,
    saveTags,
    saveAttachments,
    setErrors,
    setShowCompletePhaseModal,
    handleRefresh,
    handleCloseTaskPanel,
  });

  // Updates current step phase & phase key dates for dropdown
  useEffect(() => {
    const phase = currentTask.stepUuid
      ? workflow?.steps.find((step) => step.uuid === currentTask?.stepUuid)?.phase
      : task?.step?.phase;

    setCurrentTask({ ...currentTask, phase: phase ?? null });

    if (phase) {
      updateStepsSelectDataByPhase(phase);
    }
    // NOTE - hiding this for now KAN-7325 - https://kanaearth.atlassian.net/browse/KAN-7325
    // setPhaseKeyDatesSelectData(TemplateTaskConstants.PHASE_KEY_DATES_SELECT_DATA_MAP[phase]);
  }, [task?.step?.phase]);

  useEffect(() => {
    if (show) {
      const currentPhase = workflow?.steps.find((step) => step.uuid === currentStepUuid)?.phase;
      if (currentPhase) updateStepsSelectDataByPhase(currentPhase);
      setCurrentTask((prev) =>
        mode.kind === "create"
          ? {
              ...TaskConstants.DEFAULT_TASK,
              // General tasks are owned by the creating org, as are the parents that fix the owner.
              ownerOrganisationUuid:
                isGeneralTask || ownerOrganisationIsFixed ? (currentOrganisationUuid ?? null) : null,
              stepUuid: isGeneralTask ? null : (currentStepUuid ?? null),
              phase: isGeneralTask ? null : (currentPhase ?? null),
            }
          : // Re-seeding the task already on screen keeps the audience its ACL resolved (and any edit to
            // it); only a different task starts unresolved and waits for its own ACL read.
            mapTaskToPanelData(mode.task, prev.uuid === mode.task.uuid ? prev.visibility : null)
      );
    }
  }, [currentOrganisationUuid, currentStepUuid, ownerOrganisationIsFixed, isGeneralTask, show, mode, workflow?.steps]);

  // The Step picker follows the phase in view; the workflow's own options load in useTaskStepOptions.
  useEffect(() => {
    if (currentTask.phase !== null) {
      updateStepsSelectDataByPhase(currentTask.phase);
    }
  }, [workflow?.uuid, refresh, currentTask.phase, updateStepsSelectDataByPhase]);

  /*
    Whether the acting org may put a document into the parent's store at all — a different right from editing
    the task (KAN-9638). Without it the API refuses the upload, so offering one shows an error instead of an
    attachment. The policy's document scope is exactly the hook's parent scope, so it is handed over as-is.
  */
  const { canUploadDocument } = useDocumentUploadPermission(
    /*
      Only while the panel is open. Hosts render this panel unconditionally and hand it a fixed parent, hiding
      it with `show` alone, so without this the grant would be read on page load for a panel nobody opened —
      announcing a failure the user has no context for, and leaving that verdict in place for the whole page,
      since opening the panel changes no other input to the read.
    */
    show && parentPolicy && generalTaskParent.uuid
      ? { scope: parentPolicy.documents.scope, uuid: generalTaskParent.uuid }
      : undefined,
    user?.userUuid
  );

  // Detaching needs only edit rights and a parent that can hold documents — no upload modal is involved.
  const canRemoveAttachment = hasFullEditAccess && attachmentParentType !== undefined;

  /*
    Adding needs one thing more: the uuid of the parent to upload against, which every scope takes as a prop.
    A project upload also used to require the panel to sit inside ProjectProvider, since the modal read the
    project's document types and group from ProjectContext — which hid the whole section on every other host
    (the dashboards, the tasks board, the activities dashboard) for a task with no attachments yet. The modal
    now reads the project itself when it has no context, so the offer no longer depends on the host.
  */
  const canAddAttachment = canRemoveAttachment && generalTaskParent.uuid != null && canUploadDocument;

  return {
    isCreateTask: mode.kind === "create",
    isVisibilityPinnedToCurrentOrg,
    ownerOrganisationIsFixed,
    isGeneralTask,
    currentUserType,
    parentObjectType,

    // phaseKeyDatesSelectData,
    stepSelectData,
    organisationsSelectData,
    visibilityOrganisationsSelectData,
    activitySelectData,

    errors,
    setErrors,

    currentTask,
    setCurrentTask,

    tags,
    linkedTags,
    availableTags,
    setNewTagName,
    handleToggleTag,
    handleRemoveTag,
    handleAddNewTag,

    attachments,
    isAttachmentsLoading,
    canAddAttachment,
    canRemoveAttachment,
    attachmentParentType,
    attachmentParentUuid: generalTaskParent.uuid,
    beginUpload,
    handleDocumentsUploaded,
    handleRemoveAttachment,
    handleDownloadAttachment,

    isTitleEditable,
    setIsTitleEditable,

    showDeleteTaskModal,
    setShowDeleteTaskModal,

    showCompletePhaseModal,
    setShowCompletePhaseModal,

    updateStepsSelectDataByPhase,
    handleLinkedActivityOnClick,
    handleCloseTaskPanel,
    handleSubmit,
    isHandleSubmitLoading,
    defaultSelectedOrgTypeVisibility,

    workflowOrganisationAccessLevel: effectiveAccessLevel,
    hasFullEditAccess,
  };
};
